/**
 * Utilitaires de formatage des nombres — convention Bedee
 * Séparateur de milliers : point "."
 * Séparateur décimal    : virgule ","
 * Exemple : 10 234,56 → 10.234,56
 */

import { parsePhoneNumber } from 'libphonenumber-js';

/**
 * Détecte le pays par défaut d'un numéro local français (10 chiffres commençant par 0).
 * Couvre les DOM-TOM pour éviter qu'un 0690 soit interprété comme un mobile métropolitain.
 */
const _defaultCountry = (clean) => {
  if (!clean.startsWith('0') || clean.length !== 10) return 'FR';
  const p = clean.slice(1, 4);
  if (['590', '690', '691'].includes(p)) return 'GP'; // Guadeloupe
  if (['596', '696', '697'].includes(p)) return 'MQ'; // Martinique
  if (['594', '694', '695'].includes(p)) return 'GF'; // Guyane
  if (['262', '692', '693'].includes(p)) return 'RE'; // Réunion
  if (['269', '639'].includes(p)) return 'YT';        // Mayotte
  if (['508'].includes(p)) return 'PM';               // Saint-Pierre-et-Miquelon
  return 'FR';
};

/**
 * Normalise un numéro pour STOCKAGE en base : format E.164 brut, sans espaces.
 * Recommandé pour les API SMS/WhatsApp/Twilio etc.
 *
 * Exemples :
 *   0611223344      → +33611223344
 *   06 11 22 33 44  → +33611223344
 *   +33 6 11 22 33  → +33611223344  (nettoyage des espaces)
 *   0690450505      → +590690450505 (Guadeloupe)
 */
export const normalizePhone = (raw) => {
  if (!raw) return raw;
  const clean = String(raw).replace(/[\s\-\.\(\)]/g, '');
  try {
    const country = clean.startsWith('+') ? undefined : _defaultCountry(clean);
    const parsed = parsePhoneNumber(clean, country);
    if (parsed && parsed.isValid()) return parsed.format('E.164');
  } catch (_) { /* numéro non reconnu */ }
  return raw;
};

/**
 * Formate un numéro pour AFFICHAGE uniquement — ne jamais stocker ce résultat.
 * Utilise Google libphonenumber-js — même moteur qu'Android/iOS.
 *
 * Exemples :
 *   +33611223344    → +33 6 11 22 33 44
 *   +590690450505   → +590 690 45 05 05  (Guadeloupe)
 *   0611223344      → +33 6 11 22 33 44  (normalise à la volée)
 */
export const formatPhone = (raw) => {
  if (!raw) return '';
  const clean = String(raw).replace(/[\s\-\.\(\)]/g, '');
  try {
    const country = clean.startsWith('+') ? undefined : _defaultCountry(clean);
    const parsed = parsePhoneNumber(clean, country);
    if (parsed && parsed.isValid()) {
      return parsed.formatInternational();
    }
  } catch (_) { /* numéro non reconnu, retourner brut */ }
  return raw;
};

/**
 * Formate un prix au format 10.000,00
 */
export const formatPrice = (n, decimals = 2) => {
  if (n == null || n === '' || isNaN(Number(n))) return '—';
  return Number(n)
    .toLocaleString('fr-FR', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    })
    .replace(/[\u00A0\u202F ]/g, '.'); // narrow/no-break space → point
};

/**
 * Formate une quantité (même format, sans €)
 */
export const formatQty = (n, decimals = 2) => formatPrice(n, decimals);

/**
 * Parse une saisie au format français (virgule = décimale, point = milliers)
 * Exemples : "10.234,56" → 10234.56 | "45000,5" → 45000.5 | "45000" → 45000
 * Si pas de virgule, le point est traité comme décimale (valeur venant de l'API/JS)
 * Exemple : "15.88" → 15.88 | "1.234,56" → 1234.56
 */
export const parsePrice = (str) => {
  if (str == null || str === '') return null;
  const s = String(str).trim();
  let cleaned;
  if (s.includes(',')) {
    // Format français : points = milliers, virgule = décimale
    cleaned = s.replace(/\./g, '').replace(',', '.');
  } else {
    // Pas de virgule : le point est le séparateur décimal (float JS / API)
    cleaned = s;
  }
  const n = parseFloat(cleaned);
  return isNaN(n) ? null : n;
};
